'use client';

import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Check, RotateCcw, ArrowDownUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textReverserContent } from '../../data/text-reverser-content';
import Link from 'next/link';

export default function TextReverser() {
    const [inputText, setInputText] = useState('');
    const [copied, setCopied] = useState(false);
    const [preserveSpacing, setPreserveSpacing] = useState(true);
    const [reverseWords, setReverseWords] = useState(false);
    const { toast } = useToast();

    const reversedText = useMemo(() => {
        if (!inputText) return '';

        if (reverseWords) {
            // Reverse the order of words but keep characters in each word in order
            return inputText
                .split('\n')
                .map(line =>
                    line
                        .split(' ')
                        .reverse()
                        .join(' ')
                )
                .join('\n');
        }

        // Reverse all characters
        return preserveSpacing
            ? inputText.split('').reverse().join('')
            : inputText.split('').filter(c => c !== ' ' && c !== '\n').reverse().join('');
    }, [inputText, preserveSpacing, reverseWords]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileContent = event.target?.result as string;
            setInputText(fileContent);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(reversedText);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Reversed text has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const clearText = () => setInputText('');
    const swapText = () => {
        setInputText(reversedText);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Text Reverser">
                Reverse text strings instantly with our free online tool. Reverse words, lines, or entire text for creative effects and data processing.
            </ToolPageIntro>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Original Text</CardTitle>
                            <CardDescription>Enter or paste your text below</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    placeholder="Type or paste your text here..."
                                    className="min-h-[200px] w-full pl-3"
                                    rows={8}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearText}
                                        disabled={!inputText}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Clear text"
                                    >
                                        <RotateCcw className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="relative">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        asChild
                                        className="cursor-pointer"
                                    >
                                        <Label htmlFor="file-upload" className="cursor-pointer">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="mr-2 h-4 w-4"
                                            >
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="17 8 12 3 7 8" />
                                                <line x1="12" y1="3" x2="12" y2="15" />
                                            </svg>
                                            Upload File
                                            <input
                                                id="file-upload"
                                                type="file"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                accept=".txt,.md,.doc,.docx"
                                            />
                                        </Label>
                                    </Button>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {inputText.length} characters • {inputText.split(/\s+/).filter(Boolean).length} words
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Options</CardTitle>
                            <CardDescription>Customize how text is reversed</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label htmlFor="preserve-spacing">Preserve spacing</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Keep spaces and line breaks when reversing
                                    </p>
                                </div>
                                <Switch
                                    id="preserve-spacing"
                                    checked={preserveSpacing}
                                    onCheckedChange={setPreserveSpacing}
                                    disabled={reverseWords}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                    <Label htmlFor="reverse-words">Reverse word order</Label>
                                    <p className="text-sm text-muted-foreground">
                                        Reverse the order of words instead of characters
                                    </p>
                                </div>
                                <Switch
                                    id="reverse-words"
                                    checked={reverseWords}
                                    onCheckedChange={(checked) => {
                                        setReverseWords(checked);
                                        if (checked) setPreserveSpacing(true);
                                    }}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle>Reversed Text</CardTitle>
                                    <CardDescription>
                                        {reverseWords ? 'Words in reverse order' : 'Characters in reverse order'}
                                    </CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={swapText}
                                    disabled={!inputText}
                                    className="h-9"
                                >
                                    <ArrowDownUp className="h-4 w-4 mr-2" />
                                    Swap Text
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={reversedText}
                                    readOnly
                                    className="min-h-[200px] w-full bg-muted/50 font-mono pl-3"
                                    rows={8}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={copyToClipboard}
                                        disabled={!reversedText}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Copy to clipboard"
                                    >
                                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {reversedText.length} characters • {reversedText.split(/\s+/).filter(Boolean).length} words
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>About Text Reverser</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm text-muted-foreground">
                            <p>
                                This tool helps you reverse text in different ways. You can reverse the order of characters or words,
                                and choose whether to preserve spaces and line breaks.
                            </p>

                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">Features:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Reverse character order (with or without spaces)</li>
                                    <li>Reverse word order while keeping words intact</li>
                                    <li>Copy results to clipboard with one click</li>
                                    <li>Upload text from files</li>
                                    <li>Swap between original and reversed text</li>
                                </ul>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-medium text-foreground">Tips:</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Use <strong>Preserve spacing</strong> to keep the original formatting when reversing characters</li>
                                    <li>Try <strong>Reverse word order</strong> to quickly change the sequence of words in your text</li>
                                    <li>Click <strong>Swap Text</strong> to move the reversed text back to the input</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={textReverserContent.title}
                description={textReverserContent.description}
                content={textReverserContent.content}
                faqs={textReverserContent.faqs}
                relatedTools={textReverserContent.relatedTools}
            />
        </div>
    );
}
